import { Injectable } from '@angular/core';
import { HttpRequest, HttpResponse } from '@angular/common/http';
import { CacheConfig, CacheEntry, CacheStats, EndpointStats } from '../../models/cache-config.model';

@Injectable({
  providedIn: 'root'
})
export class CacheService {
  private cache = new Map<string, CacheEntry>();
  private stats: CacheStats = {
    hits: 0,
    misses: 0,
    size: 0,
    endpoints: new Map<string, EndpointStats>()
  };

  // Configuration for cached endpoints
  private cacheConfig: CacheConfig[] = [
    {
      endpoint: '/getMedCarts',
      cacheKey: (req: HttpRequest<any>) => {
        const body = req.body as any;
        const params = req.params;
        const communityId = body?.communityId || params.get('communityId');
        return `community_${communityId}`;
      }
    },
    {
      endpoint: '/getCommunityReportingDates',
      cacheKey: (req: HttpRequest<any>) => {
        const body = req.body as any;
        const params = req.params;
        const communityId = body?.communityId || params.get('communityId');
        return `reporting_dates_${communityId}`;
      }
    },
    // Add more endpoints here as needed
  ];

  constructor() {
    // Cache is automatically cleared on page reload (in-memory storage)
    this.initializeStats();
  }

  private initializeStats(): void {
    this.cacheConfig.forEach(config => {
      this.stats.endpoints.set(config.endpoint, {
        hits: 0,
        misses: 0,
        avgResponseTime: 0,
        lastCached: 0
      });
    });
  }

  getCacheConfig(url: string): CacheConfig | undefined {
    return this.cacheConfig.find(config =>
      url.includes(config.endpoint)
    );
  }

  generateCacheKey(req: HttpRequest<any>, config: CacheConfig): string {
    // Use custom cache key generator if provided
    if (config.cacheKey) {
      return config.cacheKey(req);
    }

    // Default cache key generation
    const params = req.params.keys().sort()
      .map(k => `${k}=${req.params.get(k)}`)
      .join('&');

    let body = '';
    if (req.method === 'POST' && req.body) {
      try {
        body = JSON.stringify(req.body);
      } catch (e) {
        console.warn('Unable to stringify request body for cache key', e);
      }
    }

    return `${req.method}:${req.url}${params ? '?' + params : ''}${body ? ':' + body : ''}`;
  }

  get(key: string): HttpResponse<any> | null {
    const entry = this.cache.get(key);
    if (!entry) {
      return null;
    }

    // Check TTL if specified
    if (entry.ttl) {
      const elapsed = Date.now() - entry.timestamp;
      if (elapsed > entry.ttl) {
        this.cache.delete(key);
        this.stats.size = this.cache.size;
        return null;
      }
    }

    this.stats.hits++;
    return entry.response.clone();
  }

  set(key: string, response: HttpResponse<any>, ttl?: number, endpoint?: string): void {
    this.cache.set(key, {
      response: response.clone(),
      timestamp: Date.now(),
      ttl
    });

    this.stats.size = this.cache.size;

    // Update endpoint stats
    if (endpoint) {
      const endpointStats = this.stats.endpoints.get(endpoint);
      if (endpointStats) {
        endpointStats.lastCached = Date.now();
      }
    }
  }

  recordMiss(endpoint: string): void {
    this.stats.misses++;
    const endpointStats = this.stats.endpoints.get(endpoint);
    if (endpointStats) {
      endpointStats.misses++;
    }
  }

  recordHit(endpoint: string): void {
    const endpointStats = this.stats.endpoints.get(endpoint);
    if (endpointStats) {
      endpointStats.hits++;
    }
  }

  delete(key: string): boolean {
    const deleted = this.cache.delete(key);
    this.stats.size = this.cache.size;
    return deleted;
  }

  clear(): void {
    this.cache.clear();
    this.stats.size = 0;
  }

  clearByPattern(pattern: string): void {
    const keys = Array.from(this.cache.keys());
    keys.forEach(key => {
      if (key.includes(pattern)) {
        this.cache.delete(key);
      }
    });
    this.stats.size = this.cache.size;
  }

  clearByEndpoint(endpoint: string): void {
    const keys = Array.from(this.cache.keys());
    keys.forEach(key => {
      if (key.includes(endpoint)) {
        this.cache.delete(key);
      }
    });
    this.stats.size = this.cache.size;
  }

  getStats(): CacheStats {
    return {
      ...this.stats,
      endpoints: new Map(this.stats.endpoints)
    };
  }

  // Method to add new cache configurations dynamically
  addCacheConfig(config: CacheConfig): void {
    const existingIndex = this.cacheConfig.findIndex(c => c.endpoint === config.endpoint);
    if (existingIndex >= 0) {
      this.cacheConfig[existingIndex] = config;
    } else {
      this.cacheConfig.push(config);
      this.stats.endpoints.set(config.endpoint, {
        hits: 0,
        misses: 0,
        avgResponseTime: 0,
        lastCached: 0
      });
    }
  }

  // Method to remove cache configuration
  removeCacheConfig(endpoint: string): void {
    this.cacheConfig = this.cacheConfig.filter(c => c.endpoint !== endpoint);
    this.stats.endpoints.delete(endpoint);
    this.clearByEndpoint(endpoint);
  }

  // Get all configured endpoints
  getConfiguredEndpoints(): string[] {
    return this.cacheConfig.map(c => c.endpoint);
  }
}
